import Component from '@glimmer/component';
import { service } from '@ember/service';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import {
  HdsCardContainer,
  HdsIcon,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import { Flex } from 'core/components';
import { classNames } from 'core/helpers';
import { boxPadding } from 'core/modifiers';

/**
 * The project object.
 * @argument project
 * @type {Object}
 */
/**
 *
 * `PageProjectsListLastOpenedCard` shows the name and description of a project
 * with a link to the project detail.
 *
 *
 * ```
 * <Page::Projects::List::LastOpenedCard
 *   @project={{this.project}}
 * />
 * ```
 *
 * @class PageProjectsListLastOpenedCard
 *
 */
export default class PageProjectsListLastOpenedCard extends Component {
  @service analytics;

  goToLastOpened = () => {
    this.analytics.trackEvent('external_ui_hashicorp_project_last_opened');
  };

  <template>
    <HdsCardContainer
      @level="base"
      @hasBorder="true"
      class="hcp-flex"
      {{boxPadding "lg"}}
      ...attributes
    >
      <Flex @direction="column" @justifyContent="space-between" @grow={{1}}>
        <Flex @direction="column" @gap="md" @grow={{1}}>
          <Flex @alignItems="center" @direction="row" @gap="xs">
            <HdsIcon
              @name="eye"
              @isInline={{true}}
              data-test-projects-list-last-opened-card-icon
            />
            <span
              class={{classNames
                "hds-typography-body-200"
                "hds-foreground-strong"
                "hds-font-weight-regular"
              }}
              data-test-projects-list-last-opened-card-last-opened-label
            >
              {{t "components.page.projects.list.last-opened-project"}}
            </span>
          </Flex>
          <Flex @alignItems="start" @direction="row" @gap="md" @grow={{1}}>
            <Flex @grow={{1}} @direction="column" @gap="2xs">
              <h3
                class={{classNames
                  "hds-typography-display-300"
                  "hds-font-weight-semibold"
                  "hds-foreground-strong"
                }}
                data-test-projects-list-last-opened-card-header
              >
                {{@project.name}}
              </h3>
              {{#if @project.description}}
                <p
                  class={{classNames
                    "hds-typography-body-200"
                    "hds-foreground-faint"
                    "hds-font-weight-regular"
                  }}
                  data-test-projects-list-last-opened-card-description
                >
                  {{@project.description}}
                </p>
              {{/if}}
            </Flex>
            <Flex @grow={{0}} @shrink={{0}} @justifyContent="end">
              <HdsLinkStandalone
                @icon="file-text"
                @models={{array @project.parent.id @project.id}}
                @route="cloud.orgs.detail.projects.detail"
                @text={{t "components.page.projects.list.view-project"}}
                {{on "click" this.goToLastOpened}}
                data-test-projects-list-last-opened-card-link
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </HdsCardContainer>
  </template>
}
